import { useState, useEffect, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { PublicKey } from '@solana/web3.js';
import { type TokenAnalysis } from '../lib/api';
import {
  executeTrade,
  getTradeQuote,
  formatTokenAmount,
  getTokenBalance,
  getSolBalance,
  detectPlatform,
  PRIORITY_FEES,
  type TradeQuote,
  type PriorityLevel,
  type Platform,
} from '../lib/trading';
import {
  addTransaction,
  updateTransactionBySignature,
  getTokenTransactions,
  formatTimestamp,
  type TransactionRecord,
} from '../lib/history';
import { getTradingFeeBps, getFeeDisplayString } from '../lib/subscription';
import { SimulationPanel } from './SimulationPanel';

interface Props {
  token: TokenAnalysis;
  connected: boolean;
}

const AMOUNTS = [0.1, 0.5, 1.0];
const SLIPPAGE_PRESETS = [1, 5, 10];
const SLIPPAGE_STORAGE_KEY = 'skop_slippage';
const PRIORITY_STORAGE_KEY = 'skop_priority';
const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true' || import.meta.env.DEV;

const PLATFORM_LABELS: Record<Platform, string> = {
  'pump.fun': 'pump.fun',
  'pumpswap': 'PumpSwap',
  'letsbonk': 'LetsBONK',
  'raydium': 'Raydium',
  'jupiter': 'Jupiter',
  'unknown': 'Unknown',
};

const PLATFORM_COLORS: Record<Platform, string> = {
  'pump.fun': '#00ff88',
  'pumpswap': '#00ff88',
  'letsbonk': '#ffb800',
  'raydium': '#00d1ff',
  'jupiter': '#c7f284',
  'unknown': '#888',
};

function getStoredSlippage(): number {
  try {
    const stored = localStorage.getItem(SLIPPAGE_STORAGE_KEY);
    if (stored) {
      const val = parseFloat(stored);
      if (!isNaN(val) && val > 0 && val <= 100) return val;
    }
  } catch {}
  return 5;
}

function getStoredPriority(): PriorityLevel {
  try {
    const stored = localStorage.getItem(PRIORITY_STORAGE_KEY);
    if (stored && stored in PRIORITY_FEES) {
      return stored as PriorityLevel;
    }
  } catch {}
  return 'medium';
}

export function TradePanel({ token, connected }: Props) {
  const { publicKey, signTransaction } = useWallet();
  const { setVisible } = useWalletModal();

  // Trade state
  const [amount, setAmount] = useState<number>(0.1);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [mode, setMode] = useState<'buy' | 'sell'>('buy');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [lastTxSignature, setLastTxSignature] = useState<string | null>(null);

  // Settings
  const [slippage, setSlippage] = useState<number>(getStoredSlippage);
  const [customSlippage, setCustomSlippage] = useState<string>('');
  const [showSlippageSettings, setShowSlippageSettings] = useState(false);
  const [priorityLevel, setPriorityLevel] = useState<PriorityLevel>(getStoredPriority);
  const [showPrioritySettings, setShowPrioritySettings] = useState(false);

  // Quote & balance state
  const [quote, setQuote] = useState<TradeQuote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [solBalance, setSolBalance] = useState<number | null>(null);
  const [tokenBalance, setTokenBalance] = useState<number | null>(null);
  const [detectedPlatform, setDetectedPlatform] = useState<Platform>(token.platform as Platform);

  // History
  const [recentTxs, setRecentTxs] = useState<TransactionRecord[]>([]);

  const activeAmount = customAmount ? parseFloat(customAmount) : amount;
  const activeSlippage = customSlippage ? parseFloat(customSlippage) : slippage;

  // Persist preferences
  useEffect(() => {
    try {
      localStorage.setItem(SLIPPAGE_STORAGE_KEY, String(activeSlippage));
    } catch {}
  }, [activeSlippage]);

  useEffect(() => {
    try {
      localStorage.setItem(PRIORITY_STORAGE_KEY, priorityLevel);
    } catch {}
  }, [priorityLevel]);

  // Fetch balances when wallet connects
  useEffect(() => {
    if (!publicKey || USE_MOCK) return;

    const fetchBalances = async () => {
      try {
        const [sol, tokenBal] = await Promise.all([
          getSolBalance(publicKey),
          getTokenBalance(publicKey, new PublicKey(token.address)),
        ]);
        setSolBalance(sol);
        setTokenBalance(tokenBal.balance);
      } catch (e) {
        console.error('Failed to fetch balances:', e);
      }
    };

    fetchBalances();
    const interval = setInterval(fetchBalances, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [publicKey, token.address]);

  // Detect platform
  useEffect(() => {
    if (USE_MOCK) return;

    detectPlatform(token.address).then(info => {
      setDetectedPlatform(info.platform);
    }).catch(console.error);
  }, [token.address]);

  // Load transaction history
  useEffect(() => {
    const txs = getTokenTransactions(token.address);
    setRecentTxs(txs.slice(0, 5));
  }, [token.address, success]);

  // Fetch quote
  const fetchQuote = useCallback(async () => {
    if (!activeAmount || activeAmount <= 0 || USE_MOCK) {
      setQuote(null);
      return;
    }

    setQuoteLoading(true);
    try {
      const result = await getTradeQuote({
        tokenAddress: token.address,
        action: mode,
        amount: activeAmount,
        slippageBps: Math.round(activeSlippage * 100),
        platform: detectedPlatform,
      });
      setQuote(result);
    } catch (err) {
      console.error('Failed to fetch quote:', err);
      setQuote(null);
    } finally {
      setQuoteLoading(false);
    }
  }, [activeAmount, mode, activeSlippage, token.address, detectedPlatform]);

  useEffect(() => {
    const timer = setTimeout(fetchQuote, 300);
    return () => clearTimeout(timer);
  }, [fetchQuote]);

  const handleTrade = async () => {
    if (!connected) {
      setVisible(true);
      return;
    }

    if (!publicKey || !signTransaction) {
      setError('Wallet not connected');
      return;
    }

    if (!activeAmount || activeAmount <= 0) {
      setError('Enter a valid amount');
      return;
    }

    // Check balance
    if (mode === 'buy' && solBalance !== null && activeAmount > solBalance - 0.01) {
      setError('Insufficient SOL balance');
      return;
    }

    if (mode === 'sell' && tokenBalance !== null && activeAmount > tokenBalance) {
      setError('Insufficient token balance');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    // Add pending transaction to history
    const pendingTx = addTransaction({
      signature: '',
      tokenAddress: token.address,
      tokenSymbol: token.symbol,
      tokenName: token.name,
      action: mode,
      inputAmount: activeAmount,
      outputAmount: quote?.expectedOutput.toString() || '0',
      platform: detectedPlatform,
      status: 'pending',
      explorerUrl: '',
    });

    try {
      const result = await executeTrade({
        tokenAddress: token.address,
        action: mode,
        amount: activeAmount,
        userWallet: publicKey,
        signTransaction,
        slippageBps: Math.round(activeSlippage * 100),
        priorityLevel,
        platform: detectedPlatform,
      });

      // Update transaction record
      updateTransactionBySignature(pendingTx.signature || pendingTx.id, {
        status: 'confirmed',
        confirmedAt: Date.now(),
      });

      // Update with actual signature
      pendingTx.signature = result.signature;
      pendingTx.explorerUrl = result.explorerUrl;

      setSuccess(`${mode === 'buy' ? 'Bought' : 'Sold'} successfully!`);
      setLastTxSignature(result.signature);

      // Refresh balances
      if (publicKey) {
        const [sol, tokenBal] = await Promise.all([
          getSolBalance(publicKey),
          getTokenBalance(publicKey, new PublicKey(token.address)),
        ]);
        setSolBalance(sol);
        setTokenBalance(tokenBal.balance);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Trade failed';
      setError(message);

      // Update transaction as failed
      updateTransactionBySignature(pendingTx.signature || pendingTx.id, {
        status: 'failed',
        errorMessage: message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate flat 1% fee
  const walletAddress = publicKey?.toString() ?? null;
  const feeBps = getTradingFeeBps();
  const skopFee = activeAmount * (feeBps / 10000);
  const priorityFeeEstimate = (PRIORITY_FEES[priorityLevel] * 200) / 1e9;

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      background: 'var(--bg-secondary)',
      borderTop: '1px solid var(--border)',
      padding: '16px',
      paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
    }}>
      <div style={{ maxWidth: '480px', margin: '0 auto' }}>
        {/* Platform Badge */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
        }}>
          <span style={{
            padding: '4px 8px',
            borderRadius: 'var(--radius-sm)',
            background: `${PLATFORM_COLORS[detectedPlatform]}20`,
            color: PLATFORM_COLORS[detectedPlatform],
            fontSize: '11px',
            fontWeight: 600,
          }}>
            {PLATFORM_LABELS[detectedPlatform]}
          </span>
          {solBalance !== null && (
            <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
              Balance: {solBalance.toFixed(4)} SOL
            </span>
          )}
        </div>

        {/* Buy/Sell Toggle */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <button
            onClick={() => setMode('buy')}
            className={`btn ${mode === 'buy' ? 'btn-success' : 'btn-secondary'}`}
            style={{ flex: 1 }}
          >
            Buy
          </button>
          <button
            onClick={() => setMode('sell')}
            className={`btn ${mode === 'sell' ? 'btn-danger' : 'btn-secondary'}`}
            style={{ flex: 1 }}
          >
            Sell
          </button>
        </div>

        {/* Token Balance for Sell Mode */}
        {mode === 'sell' && tokenBalance !== null && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            background: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius-md)',
            marginBottom: '12px',
          }}>
            <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
              You have:
            </span>
            <button
              onClick={() => setCustomAmount(tokenBalance.toString())}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: 'var(--accent)',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              {tokenBalance.toLocaleString()} {token.symbol} (Max)
            </button>
          </div>
        )}

        {/* Amount Buttons */}
        <div className="amount-buttons" style={{ marginBottom: '12px' }}>
          {mode === 'buy' ? (
            <>
              {AMOUNTS.map((a) => (
                <button
                  key={a}
                  onClick={() => {
                    setAmount(a);
                    setCustomAmount('');
                  }}
                  className={`amount-btn ${amount === a && !customAmount ? 'active' : ''}`}
                >
                  {a} SOL
                </button>
              ))}
            </>
          ) : (
            <>
              {[25, 50, 100].map((pct) => (
                <button
                  key={pct}
                  onClick={() => {
                    if (tokenBalance) {
                      setCustomAmount((tokenBalance * pct / 100).toString());
                    }
                  }}
                  className="amount-btn"
                >
                  {pct}%
                </button>
              ))}
            </>
          )}
          <input
            type="number"
            placeholder={mode === 'buy' ? 'SOL' : 'Tokens'}
            value={customAmount}
            onChange={(e) => setCustomAmount(e.target.value)}
            className="input"
            style={{
              flex: 1,
              minWidth: '80px',
              textAlign: 'center',
              padding: '12px 8px',
            }}
          />
        </div>

        {/* Settings Row */}
        <div style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '12px',
        }}>
          {/* Slippage Toggle */}
          <button
            onClick={() => {
              setShowSlippageSettings(!showSlippageSettings);
              setShowPrioritySettings(false);
            }}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--text-secondary)',
              fontSize: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <span style={{
              transform: showSlippageSettings ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              display: 'inline-block',
              fontSize: '10px',
            }}>▶</span>
            Slippage: {activeSlippage}%
            {activeSlippage > 15 && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--risk-high)" strokeWidth="2" style={{ marginLeft: '4px' }}>
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            )}
          </button>

          {/* Priority Toggle */}
          <button
            onClick={() => {
              setShowPrioritySettings(!showPrioritySettings);
              setShowSlippageSettings(false);
            }}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--text-secondary)',
              fontSize: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <span style={{
              transform: showPrioritySettings ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              display: 'inline-block',
              fontSize: '10px',
            }}>▶</span>
            Priority: {priorityLevel}
          </button>
        </div>

        {/* Slippage Settings Panel */}
        {showSlippageSettings && (
          <div style={{
            background: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius-md)',
            padding: '12px',
            marginBottom: '12px',
          }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              {SLIPPAGE_PRESETS.map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setSlippage(s);
                    setCustomSlippage('');
                  }}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: slippage === s && !customSlippage
                      ? '1px solid var(--accent)'
                      : '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    background: slippage === s && !customSlippage
                      ? 'rgba(0, 240, 255, 0.1)'
                      : 'transparent',
                    color: slippage === s && !customSlippage
                      ? 'var(--accent)'
                      : 'var(--text-secondary)',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  {s}%
                </button>
              ))}
              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  type="number"
                  placeholder="Custom"
                  value={customSlippage}
                  onChange={(e) => setCustomSlippage(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    paddingRight: '24px',
                    border: customSlippage
                      ? '1px solid var(--accent)'
                      : '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'transparent',
                    color: 'var(--text-primary)',
                    fontSize: '13px',
                    textAlign: 'center',
                  }}
                  min="0.1"
                  max="100"
                  step="0.1"
                />
                <span style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-tertiary)',
                  fontSize: '13px',
                  pointerEvents: 'none',
                }}>%</span>
              </div>
            </div>
            <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: 0 }}>
              Transaction fails if price moves more than {activeSlippage}%
            </p>
          </div>
        )}

        {/* Priority Settings Panel */}
        {showPrioritySettings && (
          <div style={{
            background: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius-md)',
            padding: '12px',
            marginBottom: '12px',
          }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              {(['none', 'low', 'medium', 'high', 'turbo'] as PriorityLevel[]).map((level) => (
                <button
                  key={level}
                  onClick={() => setPriorityLevel(level)}
                  style={{
                    flex: 1,
                    padding: '8px 4px',
                    border: priorityLevel === level
                      ? '1px solid var(--accent)'
                      : '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    background: priorityLevel === level
                      ? 'rgba(0, 240, 255, 0.1)'
                      : 'transparent',
                    color: priorityLevel === level
                      ? 'var(--accent)'
                      : 'var(--text-secondary)',
                    fontSize: '11px',
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                  }}
                >
                  {level}
                </button>
              ))}
            </div>
            <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: 0 }}>
              Priority fee: ~{priorityFeeEstimate.toFixed(5)} SOL
            </p>
          </div>
        )}

        {/* Quote Display */}
        {(quote || quoteLoading) && (
          <div style={{
            background: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius-md)',
            padding: '12px',
            marginBottom: '12px',
          }}>
            {quoteLoading ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-tertiary)',
                fontSize: '13px',
              }}>
                <span className="animate-pulse">Fetching quote...</span>
              </div>
            ) : quote && (
              <>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '8px',
                }}>
                  <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                    You receive
                  </span>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                  }}>
                    {mode === 'buy'
                      ? `~${formatTokenAmount(quote.expectedOutput)} ${token.symbol}`
                      : `~${(Number(quote.expectedOutput) / 1e9).toFixed(4)} SOL`
                    }
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '8px',
                }}>
                  <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                    Minimum ({activeSlippage}% slippage)
                  </span>
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {mode === 'buy'
                      ? `${formatTokenAmount(quote.minOutput)} ${token.symbol}`
                      : `${(Number(quote.minOutput) / 1e9).toFixed(4)} SOL`
                    }
                  </span>
                </div>
                {quote.priceImpact > 1 && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                      Price impact
                    </span>
                    <span style={{
                      fontSize: '12px',
                      color: quote.priceImpact > 5 ? 'var(--risk-high)' : 'var(--risk-medium)',
                    }}>
                      {quote.priceImpact.toFixed(2)}%
                    </span>
                  </div>
                )}
                {quote.route && (
                  <div style={{
                    marginTop: '8px',
                    fontSize: '11px',
                    color: 'var(--text-tertiary)',
                  }}>
                    Route: {quote.route}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Transaction Simulation */}
        {activeAmount > 0 && walletAddress && (
          <SimulationPanel
            tokenAddress={token.address}
            action={mode}
            amount={activeAmount}
            slippageBps={Math.round(activeSlippage * 100)}
            userWallet={walletAddress}
          />
        )}

        {/* Fee Info */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '12px',
          color: 'var(--text-tertiary)',
          marginBottom: '12px',
        }}>
          <span>
            SKōP Fee ({getFeeDisplayString()})
          </span>
          <span>
            {skopFee.toFixed(4)} SOL
          </span>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div style={{
            padding: '8px 12px',
            background: 'rgba(255, 59, 59, 0.1)',
            border: '1px solid rgba(255, 59, 59, 0.3)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--error)',
            fontSize: '13px',
            marginBottom: '12px',
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            padding: '8px 12px',
            background: 'rgba(0, 255, 136, 0.1)',
            border: '1px solid rgba(0, 255, 136, 0.3)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--success)',
            fontSize: '13px',
            marginBottom: '12px',
          }}>
            {success}
            {lastTxSignature && (
              <a
                href={`https://solscan.io/tx/${lastTxSignature}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  marginLeft: '8px',
                  color: 'var(--accent)',
                  textDecoration: 'underline',
                }}
              >
                View on Solscan
              </a>
            )}
          </div>
        )}

        {/* Trade Button */}
        <button
          onClick={handleTrade}
          disabled={loading}
          className={`btn btn-lg ${mode === 'buy' ? 'btn-success' : 'btn-danger'}`}
        >
          {loading ? (
            <span className="animate-spin" style={{
              display: 'inline-block',
              width: '20px',
              height: '20px',
              border: '2px solid rgba(0,0,0,0.2)',
              borderTopColor: '#000',
              borderRadius: '50%',
            }} />
          ) : connected ? (
            mode === 'buy'
              ? `Buy ${activeAmount || 0} SOL worth`
              : `Sell ${activeAmount || 0} ${token.symbol}`
          ) : (
            'Connect Wallet'
          )}
        </button>

        {/* Risk Warning */}
        {(token.riskLevel === 'high' || token.riskLevel === 'critical') && (
          <p style={{
            fontSize: '11px',
            color: 'var(--risk-high)',
            textAlign: 'center',
            marginTop: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            This token has {token.riskLevel} risk. Trade with caution.
          </p>
        )}

        {/* Recent Transactions */}
        {recentTxs.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div style={{
              fontSize: '11px',
              color: 'var(--text-tertiary)',
              marginBottom: '8px',
            }}>
              Recent transactions
            </div>
            {recentTxs.slice(0, 3).map((tx) => (
              <div
                key={tx.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: tx.status === 'confirmed' ? 'var(--success)' :
                               tx.status === 'pending' ? 'var(--risk-medium)' : 'var(--error)',
                  }} />
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {tx.action === 'buy' ? 'Bought' : 'Sold'} {tx.inputAmount} {tx.action === 'buy' ? 'SOL' : tx.tokenSymbol}
                  </span>
                </div>
                <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                  {formatTimestamp(tx.timestamp)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
